import {FormRow} from '@dropbox/dig-components/form_row';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Typeahead} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {atoms} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {SearchLine} from '@dropbox/dig-icons/assets';
import {usePeopleTypeaheadAnalytics} from 'analytics/usePeopleTypeaheadAnalytics';
import {Employee} from 'client';
import {PEOPLE_SEARCH_RANKING_MODE, useSearchesWithStatus} from 'hooks/useEmployee';
import {t} from 'i18next';
import {useEffect, useState} from 'react';

import {Avatar} from './Avatar';

export const EmployeeTypeahead = ({
  autoFocus,
  selection,
  selections,
  placeholder,
  onSingleSelect,
  onMultiSelect,
  typeaheadContext = 'employee_picker',
}: {
  autoFocus?: boolean;
  selection?: string;
  selections?: string[];
  placeholder: Employee[];
  onSingleSelect?: (employee: Employee) => void;
  onMultiSelect?: (employees: Employee[]) => void;
  typeaheadContext?: Parameters<typeof usePeopleTypeaheadAnalytics>[0]['context'];
}) => {
  if ((selection && !onSingleSelect) || (selections && !onMultiSelect)) {
    throw new Error(
      'Selections must be used with onMultiSelect and selection must be used with onSingleSelect'
    );
  }

  const [userInputValue, setUserInputValue] = useState('');
  const [hasExpandedResults, setHasExpandedResults] = useState(false);
  const [resultsResetKey, setResultsResetKey] = useState(0);
  const resetVisibleResults = () => {
    setHasExpandedResults(false);
    setResultsResetKey((key) => key + 1);
  };
  const rankingMode = PEOPLE_SEARCH_RANKING_MODE;
  const {results: searchResult, isLoading} = useSearchesWithStatus({
    ldaps: userInputValue.split(',').map((e) => (!e.includes('@') ? e : e.trim().split('@')[0])),
  });

  const employeeHints = searchResult.length ? searchResult : placeholder;

  const filteredEmployeeHints = employeeHints.filter(
    (employee) =>
      !selections?.find((email) => email === employee.email) && employee.email !== selection
  );
  const analytics = usePeopleTypeaheadAnalytics({
    context: typeaheadContext,
    input: userInputValue,
    // Placeholder rows are local zero-query suggestions and must not become
    // typed-query impressions or selections after a zero-result response.
    results: searchResult.filter(
      (employee) =>
        !selections?.find((email) => email === employee.email) && employee.email !== selection
    ),
    isLoading,
    rankingMode,
    visibleResultCount: hasExpandedResults ? 8 : 4,
  });

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    resetVisibleResults();
    setUserInputValue(event.currentTarget.value);
  };

  const handleSelection = (ldap: string, event?: {type?: string}) => {
    const selectedEmployee = employeeHints.find((e) => e.ldap === ldap);
    if (selectedEmployee) {
      analytics.select(selectedEmployee, event?.type === 'keydown' ? 'keyboard' : 'click');
      onMultiSelect?.([selectedEmployee]);
      onSingleSelect?.(selectedEmployee);
      analytics.commit();
      resetVisibleResults();
      setUserInputValue('');
    }
  };

  useEffect(() => {
    if (userInputValue.length > 0 && userInputValue.includes(',') && searchResult.length) {
      let updatedInputValue = userInputValue;
      const foundEmployees = userInputValue.split(',').reduce((acc, value) => {
        const normalizedValue = value.trim().toLowerCase();
        const found = employeeHints.find(
          (e) =>
            e.name.toLowerCase() === normalizedValue || e.ldap === normalizedValue.split('@')[0]
        );
        if (found) {
          updatedInputValue = updatedInputValue.replace(value, '');
          acc.push(found);
        }
        return acc;
      }, [] as Employee[]);

      const newSelections = foundEmployees.filter(
        (found) => !selections?.find((e) => e === found.email)
      );

      if (newSelections.length) {
        newSelections.forEach((employee) => analytics.select(employee));
        onMultiSelect?.(newSelections);
        analytics.commit();
      }

      if (foundEmployees.length) {
        setUserInputValue(
          updatedInputValue
            .split(',')
            .filter((e) => e.length)
            .join(',')
        );
        resetVisibleResults();
      }
    }
  }, [analytics, employeeHints, onMultiSelect, searchResult, selections, userInputValue]);

  const renderTypeaheadRow = (employee: Employee) => (
    <Typeahead.Row
      key={employee.user_id}
      value={employee.ldap}
      withTitle={<Text isBold>{employee.name}</Text>}
      withSubtitle={employee.role}
      withLeftAccessory={<Avatar user={employee} />}
    />
  );

  return (
    <Typeahead.Wrapper
      onSelection={handleSelection}
      onToggle={({isOpen}) => {
        if (isOpen) analytics.start();
        else {
          analytics.dismiss();
          resetVisibleResults();
        }
      }}
    >
      {({getTriggerProps, getContentProps}) => (
        <FormRow>
          <TextInput
            autoFocus={autoFocus}
            size="large"
            placeholder={t('search_dropboxers')}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            value={userInputValue}
            withRightAccessory={
              <UIIcon src={SearchLine} className={atoms({color: 'Text Subtle'})} />
            }
            {...getTriggerProps({onChange: onInputChange, onFocus: analytics.start})}
          />

          <Typeahead.Container {...getContentProps()}>
            <Typeahead.Results
              key={resultsResetKey}
              maxResults={8}
              initialResults={hasExpandedResults ? 8 : 4}
              showMoreOnClick={() => {
                setHasExpandedResults(true);
                analytics.showMore();
              }}
              results={filteredEmployeeHints}
              renderRow={renderTypeaheadRow}
            />
          </Typeahead.Container>
        </FormRow>
      )}
    </Typeahead.Wrapper>
  );
};
